import React, { PropsWithChildren, useMemo } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { CircularProgress, dialogClasses } from '@mui/material';
import { MutationStatus } from '@tanstack/react-query';

import { IModalProps, Modal } from '@web/ui/components/modal';

import { SelectedPersonas } from './components/SelectedPersonas';

const StyledModal = styled(Modal)`
  .${dialogClasses.paperScrollBody} {
    max-width: calc(100% - 8px);
    margin-right: ${({ theme }) => theme.spacing('0500')};
  }

  .${dialogClasses.paper} {
    padding-top: ${({ theme }) => theme.spacing('3000')};
    padding-inline: ${({ theme }) => theme.spacing('2000')};
    padding-bottom: ${({ theme }) => theme.spacing('2000')};

    ${({ theme }) => theme.breakpoints.up('sm')} {
      gap: ${({ theme }) => theme.spacing('1000')};
    }
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    .${dialogClasses.paperScrollBody} {
      max-width: 480px;
      margin-right: 0;
    }

    .${dialogClasses.paper} {
      ${({ theme }) => theme.breakpoints.up('sm')} {
        gap: ${({ theme }) => theme.spacing('1000')};
      }
    }
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('2000')};
`;

const Title = styled.p`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['2']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};

  text-align: center;
`;

const RegularText = styled.p`
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  text-align: center;
`;

const Subtitle = styled(RegularText)`
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  text-align: center;
`;

const Processing = styled.div``;

const Actions = styled.div`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

export interface IBaseStakingModalProps extends Omit<IModalProps, 'title'> {
  personas: Pick<IPersonaFront, 'tokenId' | 'blockchain'>[];
  status: MutationStatus;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  completedText: React.ReactNode;
  actions: React.ReactNode[];
}

export const BaseStakingModal = ({
  personas,
  status,
  title,
  subtitle,
  completedText,
  actions,
  children,
  ...props
}: PropsWithChildren<IBaseStakingModalProps>) => {
  const bottomRow = useMemo(() => {
    switch (status) {
      case 'pending':
        return (
          <Processing>
            <CircularProgress size={24} />
          </Processing>
        );

      case 'success':
        return <RegularText>{completedText}</RegularText>;

      case 'error':
      default:
        return (
          <Actions>
            {actions.map((action, index) => (
              <React.Fragment key={index}>{action}</React.Fragment>
            ))}
          </Actions>
        );
    }
  }, [status, actions]);

  return (
    <StyledModal {...props}>
      <SelectedPersonas personas={personas} />
      <ModalContent>
        <Title>{title}</Title>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {children}
        {bottomRow}
      </ModalContent>
    </StyledModal>
  );
};
